import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, Video, Music2, Users } from "lucide-react";
import { api, fileStreamUrl } from "../lib/api";
import { useI18n } from "../i18n/i18n";

/**
 * SidebarSearch — barre de recherche globale dans la sidebar.
 * Appelle /api/search?q= (cherche dans titres, descriptions, artistes, castings)
 * Affiche un dropdown avec pistes + artistes + castings.
 * - Debounce 250ms
 * - Cache les résultats par query
 * - Entrée = navigue vers /search?q=...
 */
export const SidebarSearch = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      api.get("/search", { params: { q: s }, signal: controller.signal })
        .then(({ data }) => { setResults(data); setLoading(false); })
        .catch((e) => { if (e.name !== "CanceledError") { setLoading(false); setResults({ tracks: [], artists: [], castings: [] }); } });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const s = q.trim();
    if (!s) return;
    setOpen(false);
    onNavigate?.();
    navigate(`/explore?q=${encodeURIComponent(s)}`);
  };

  const hasResults = results && (results.tracks?.length || results.artists?.length || results.castings?.length);

  return (
    <div ref={containerRef} className="relative" data-testid="sidebar-search">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={t("action.search")}
            className="w-full rounded-full bg-white/[0.04] border border-white/10 pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-brand-orange/60 focus:bg-white/[0.06] transition"
            data-testid="sidebar-search-input"
            aria-label="Rechercher"
          />
          {loading && (
            <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 animate-spin" />
          )}
          {!loading && q && (
            <button
              type="button"
              onClick={() => { setQ(""); setResults(null); setOpen(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white/40 hover:text-white w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/10"
              aria-label="Effacer"
            >
              ✕
            </button>
          )}
        </div>
      </form>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full mt-2 z-50 bg-[#1a1411] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-[60vh] overflow-y-auto"
          data-testid="sidebar-search-dropdown"
        >
          {loading && !results && (
            <div className="p-4 text-xs text-white/50 text-center">Recherche…</div>
          )}

          {!loading && results && !hasResults && (
            <div className="p-4 text-xs text-white/50 text-center">Aucun résultat pour « {q} »</div>
          )}

          {results?.tracks?.length > 0 && (
            <Section title="Morceaux & vidéos" icon={<Video size={11} />}>
              {results.tracks.slice(0, 6).map((t) => {
                const cover = t.cover_url || (t.cover_path && !/^https?:/i.test(t.cover_path) ? fileStreamUrl(t.cover_path) : t.cover_path);
                return (
                  <Link
                    key={t.track_id}
                    to={`/track/${t.track_id}`}
                    onClick={() => { setOpen(false); onNavigate?.(); }}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition"
                    data-testid={`search-result-track-${t.track_id}`}
                  >
                    {cover ? (
                      <img src={cover} alt="" className="w-9 h-9 rounded-md object-cover shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-md bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center shrink-0">
                        {t.track_type === "video" ? <Video size={14} /> : <Music2 size={14} />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate">{t.title}</p>
                      <p className="text-[10px] text-white/50 truncate">{t.artist_name} · {t.category}</p>
                    </div>
                  </Link>
                );
              })}
            </Section>
          )}

          {results?.artists?.length > 0 && (
            <Section title="Artistes" icon={<Users size={11} />}>
              {results.artists.slice(0, 5).map((a) => (
                <Link
                  key={a.user_id}
                  to={`/artist/${a.user_id}`}
                  onClick={() => { setOpen(false); onNavigate?.(); }}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition"
                  data-testid={`search-result-artist-${a.user_id}`}
                >
                  {a.picture ? (
                    <img src={a.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-xs shrink-0">
                      {(a.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate">{a.name}</p>
                    <p className="text-[10px] text-white/50 truncate">{a.country || "—"}</p>
                  </div>
                </Link>
              ))}
            </Section>
          )}

          {q.trim().length >= 2 && (
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full py-2.5 text-center text-xs font-bold text-brand-orange hover:bg-white/5 transition border-t border-white/10"
              data-testid="sidebar-search-see-all"
            >
              Voir tous les résultats pour « {q} » →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div className="py-1">
    <div className="px-3 pt-2 pb-1 flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-white/40 font-bold">
      {icon} {title}
    </div>
    {children}
  </div>
);

export default SidebarSearch;
